import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiAlertCircle,
  FiChevronDown,
  FiChevronRight,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import Spinner from "../../components/Spinner";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../../components/lds/LdsOverview";
import {
  getChildCount,
  getClassroomId,
  getClassroomName,
  listMyClassrooms,
  type MyClassroom,
  type MyClassroomChild,
} from "../../services/myEnvironmentService";
import { translateClassName } from "../../utils/classroomUtils";
import { useCan } from "../../authz/useCan";

const IconSearch = FiSearch as any;
const IconUsers = FiUsers as any;
const IconAlert = FiAlertCircle as any;
const IconChevronDown = FiChevronDown as any;
const IconChevronRight = FiChevronRight as any;

type ClassroomStatus = "active" | "upcoming" | "previous";

const myClassroomsStyles = `
  @keyframes myClassroomsEnter {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .my-classrooms-enter {
    animation: myClassroomsEnter 420ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .my-classrooms-enter { animation: none !important; }
  }
`;

const lumenPageShell = "flex flex-col gap-6 text-gray-900 dark:text-gray-100";
const lumenMainPanel = "rounded-[32px] border border-indigo-100/60 bg-gradient-to-br from-indigo-50/90 via-white to-white p-5 shadow-[0_35px_70px_rgba(15,23,42,0.18)] backdrop-blur-sm dark:border-indigo-500/40 dark:bg-gray-900/60 dark:from-gray-900/70 dark:via-indigo-950/40 dark:to-gray-900/70 sm:p-6";
const lumenCardBase = "rounded-[32px] border bg-gradient-to-br p-5 shadow-[0_25px_70px_rgba(15,23,42,0.12)] backdrop-blur transition hover:-translate-y-0.5 hover:shadow-[0_35px_80px_rgba(15,23,42,0.16)] dark:bg-gray-900/60 dark:shadow-[0_25px_60px_rgba(0,0,0,0.45)]";
const lumenSecondaryButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-200";
const lumenPrimaryButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200";
const lumenSkyButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-sky-200/70 bg-sky-50/80 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:text-sky-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-sky-500/50 dark:bg-sky-900/40 dark:text-sky-200";

const parseLocalDate = (value: unknown): Date | null => {
  if (!value) return null;

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  if (typeof value === "number") {
    return new Date(value > 1e12 ? value : value * 1000);
  }

  if (typeof value === "string") {
    const datePart = value.includes("T") ? value.split("T")[0] : value;
    const parts = datePart.split("-");

    if (parts.length >= 3) {
      const [year, month, day] = parts;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const formatDateValue = (value: unknown): string => {
  const date = parseLocalDate(value);
  if (!date) return "";
  return date.toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

function todayDateOnly(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function getClassroomStatus(classroom: MyClassroom): ClassroomStatus {
  const today = todayDateOnly();
  const start = parseLocalDate(classroom.startDate);
  const end = parseLocalDate(classroom.endDate);

  if (end && end < today) return "previous";
  if (start && start > today) return "upcoming";
  return "active";
}

function getFamilyNames(classroom: MyClassroom): string[] {
  const names = new Set<string>();
  classroom.children?.forEach((child) => {
    const name = child.familyName?.trim();
    if (name) names.add(name);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

function getFamilyCount(classroom: MyClassroom): number {
  if (typeof classroom.familyCount === "number") return classroom.familyCount;
  return getFamilyNames(classroom).length;
}

function getClassTypeLabel(classType: unknown, t: (key: string, options?: Record<string, unknown>) => string): string {
  const normalized = String(classType || "").toUpperCase();
  if (normalized === "KINDERGARTEN") {
    return t("myEnvironment.classrooms.types.kindergarten", { defaultValue: "Kindergarten" });
  }
  if (normalized === "GRADE") {
    return t("myEnvironment.classrooms.types.grade", { defaultValue: "Grade" });
  }
  return String(classType || "");
}

function childSearchParts(child: MyClassroomChild): string[] {
  return [
    child.firstName,
    child.firstname,
    child.lastName,
    child.lastname,
    child.familyName,
    ...(child.parents ?? []).flatMap((parent) => [
      parent.firstName,
      parent.firstname,
      parent.lastName,
      parent.lastname,
      parent.email,
    ]),
  ].filter(Boolean) as string[];
}

function classroomMatches(classroom: MyClassroom, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const searchable = [
    getClassroomName(classroom),
    translateClassName(classroom),
    classroom.classType,
    classroom.schoolYearName,
    classroom.startDate,
    classroom.endDate,
    ...(classroom.children ?? []).flatMap(childSearchParts),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchable.includes(normalizedQuery);
}

const MyClassrooms: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canViewParentMeetings = useCan("my.parent-meetings", "view");
  const canManageParentMeetings = useCan("my.parent-meetings", "manage");

  const [classrooms, setClassrooms] = useState<MyClassroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [previousOpen, setPreviousOpen] = useState(false);

  const loadClassrooms = async () => {
    setLoading(true);
    setError(null);

    try {
      const rows = await listMyClassrooms();
      setClassrooms(rows);
    } catch (err) {
      console.error("Failed to load my classrooms", err);
      setError(
        t("myEnvironment.classrooms.load_failed", {
          defaultValue: "My classrooms could not be loaded.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClassrooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredClassrooms = useMemo(
    () => classrooms.filter((classroom) => classroomMatches(classroom, query)),
    [classrooms, query]
  );

  const activeClassrooms = useMemo(
    () => filteredClassrooms.filter((classroom) => getClassroomStatus(classroom) !== "previous"),
    [filteredClassrooms]
  );

  const previousClassrooms = useMemo(
    () => filteredClassrooms.filter((classroom) => getClassroomStatus(classroom) === "previous"),
    [filteredClassrooms]
  );

  const summaryClassrooms = useMemo(
    () => classrooms.filter((classroom) => getClassroomStatus(classroom) === "active"),
    [classrooms]
  );

  const totalChildren = useMemo(
    () => summaryClassrooms.reduce((sum, classroom) => sum + getChildCount(classroom), 0),
    [summaryClassrooms]
  );

  const totalFamilies = useMemo(
    () => summaryClassrooms.reduce((sum, classroom) => sum + getFamilyCount(classroom), 0),
    [summaryClassrooms]
  );

  const renderClassroomCard = (classroom: MyClassroom, status: ClassroomStatus) => {
    const id = getClassroomId(classroom);
    const name = translateClassName(classroom) || getClassroomName(classroom);
    const childCount = getChildCount(classroom);
    const familyCount = getFamilyCount(classroom);
    const parentMeetingCount = classroom.parentMeetingCount ?? 0;
    const outingCount = classroom.outingCount ?? 0;
    const isPrevious = status === "previous";
    const isUpcoming = status === "upcoming";
    const dateRange = [formatDateValue(classroom.startDate), formatDateValue(classroom.endDate)]
      .filter(Boolean)
      .join(" - ");

    return (
      <article
        key={id || name}
        className={`my-classrooms-enter flex h-full flex-col ${lumenCardBase} ${
          isPrevious
            ? "border-gray-100/70 from-gray-50/75 via-white to-white opacity-80 dark:border-gray-600/40 dark:from-gray-900/70 dark:via-gray-900/50 dark:to-gray-900/60"
            : isUpcoming
              ? "border-amber-100/70 from-amber-50/80 via-white to-white dark:border-amber-500/40 dark:from-amber-900/30 dark:via-gray-900/50 dark:to-gray-900/60"
              : "border-indigo-100/70 from-indigo-50/80 via-white to-white dark:border-indigo-500/40 dark:from-indigo-950/30 dark:via-gray-900/50 dark:to-gray-900/60"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span
                className={`lumen-glow-sheen rounded-full border px-3 py-1 text-xs font-semibold ${
                  isPrevious
                    ? "border-gray-200/70 bg-white/75 text-gray-600 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-300"
                    : isUpcoming
                      ? "border-amber-200/70 bg-amber-50/80 text-amber-800 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-200"
                      : "border-indigo-200/70 bg-indigo-50/80 text-indigo-700 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
                }`}
              >
                {isPrevious
                  ? t("myEnvironment.classrooms.previous_badge", { defaultValue: "Previous" })
                  : isUpcoming
                    ? t("myEnvironment.classrooms.upcoming_badge", { defaultValue: "Upcoming" })
                    : t("myEnvironment.classrooms.active_badge", { defaultValue: "Active" })}
              </span>
              {classroom.classType && (
                <span className="lumen-glow-sheen rounded-full border border-gray-200/70 bg-white/75 px-3 py-1 text-xs font-medium text-gray-600 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-300">
                  {getClassTypeLabel(classroom.classType, t)}
                </span>
              )}
            </div>
            <h2 className="truncate text-lg font-semibold text-gray-900 dark:text-white">{name}</h2>
            {(classroom.schoolYearName || dateRange) && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {classroom.schoolYearName || dateRange}
              </p>
            )}
          </div>

          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border border-indigo-100/70 bg-white/75 text-indigo-600 shadow-sm dark:border-indigo-500/40 dark:bg-gray-900/40 dark:text-indigo-200">
            <IconUsers className="h-5 w-5 lumen-glow-icon" />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
          {[
            [childCount, t("myEnvironment.classrooms.children", { defaultValue: "Children" })],
            [familyCount, t("myEnvironment.classrooms.families", { defaultValue: "Families" })],
            [parentMeetingCount, t("myEnvironment.classrooms.meetings", { defaultValue: "Meetings" })],
            [outingCount, t("myEnvironment.classrooms.outings", { defaultValue: "Agenda & activities" })],
          ].map(([value, label]) => (
            <div key={String(label)} className="rounded-2xl border border-white/70 bg-white/70 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/35">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-auto flex flex-wrap gap-2 pt-5">
          <button
            type="button"
            onClick={() => navigate(`/school/classrooms/${id}/details`)}
            disabled={!id}
            className={isPrevious ? lumenSecondaryButton : lumenPrimaryButton}
          >
            {t("myEnvironment.classrooms.open_classroom", { defaultValue: "Open classroom" })}
          </button>

          {!isPrevious && (canViewParentMeetings || canManageParentMeetings) && (
            <>
              {canManageParentMeetings && (
                <button
                  type="button"
                  onClick={() => navigate(`/parent-meetings/${id}/new`)}
                  disabled={!id}
                  className={lumenSecondaryButton}
                >
                  {t("myEnvironment.classrooms.plan_meeting", { defaultValue: "Plan meeting" })}
                </button>
              )}

              {canViewParentMeetings && (
                <button
                  type="button"
                  onClick={() => navigate(`/my/parent-meetings?classroomId=${id}`)}
                  disabled={!id}
                  className={lumenSkyButton}
                >
                  {t("myEnvironment.classrooms.view_meetings", { defaultValue: "View meetings" })}
                </button>
              )}
            </>
          )}
        </div>
      </article>
    );
  };

  return (
    <LdsOverviewPage className="lg:px-8" styles={<style>{myClassroomsStyles}</style>}>
      <div className={lumenPageShell}>
        <LdsOverviewHero
          icon={FiUsers}
          eyebrow={t("myEnvironment.eyebrow", { defaultValue: "Personal workspace" })}
          title={t("myEnvironment.classrooms.title", { defaultValue: "My classrooms" })}
          description={t("myEnvironment.classrooms.description", {
            defaultValue: "View your own classrooms and pupils.",
          })}
        />

        <section className={`my-classrooms-enter ${lumenMainPanel}`}>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              [
                t("myEnvironment.classrooms.active_summary", { defaultValue: "Active classrooms" }),
                summaryClassrooms.length,
                "indigo",
              ],
              [t("myEnvironment.classrooms.total_children", { defaultValue: "Children" }), totalChildren, "sky"],
              [t("myEnvironment.classrooms.total_families", { defaultValue: "Families" }), totalFamilies, "emerald"],
            ].map(([label, value, tone]) => (
              <div
                key={String(label)}
                className={`rounded-2xl border p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] ${
                  tone === "sky"
                    ? "border-sky-100/70 bg-gradient-to-br from-sky-50/80 via-white to-white dark:border-sky-500/40 dark:from-sky-900/30 dark:via-gray-900/50 dark:to-gray-900/60"
                    : tone === "emerald"
                      ? "border-emerald-100/70 bg-gradient-to-br from-emerald-50/80 via-white to-white dark:border-emerald-500/40 dark:from-emerald-900/30 dark:via-gray-900/50 dark:to-gray-900/60"
                      : "border-indigo-100/70 bg-gradient-to-br from-indigo-50/80 via-white to-white dark:border-indigo-500/40 dark:from-indigo-950/30 dark:via-gray-900/50 dark:to-gray-900/60"
                }`}
              >
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {label}
                </p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="my-classrooms-enter rounded-[32px] border border-indigo-100/60 bg-white/90 p-4 shadow-[0_25px_70px_rgba(15,23,42,0.10)] backdrop-blur dark:border-indigo-500/40 dark:bg-gray-900/60 sm:p-5">
          <div className="relative">
            <IconSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("myEnvironment.classrooms.search_placeholder", {
                defaultValue: "Search classrooms, children, families or parents",
              })}
              className="lumen-glow-input w-full py-3 pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-500 dark:text-white"
            />
          </div>
        </section>

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-100/70 bg-gradient-to-br from-red-50/80 via-white to-white p-4 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:from-red-900/30 dark:via-gray-900/50 dark:to-gray-900/60 dark:text-red-200">
            <IconAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{error}</p>
              <button
                type="button"
                onClick={loadClassrooms}
                className="mt-2 text-sm font-semibold underline underline-offset-2"
              >
                {t("common.try_again", { defaultValue: "Try again" })}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filteredClassrooms.length === 0 && (
          <div className="rounded-[32px] border border-indigo-100/60 bg-gradient-to-br from-indigo-50/80 via-white to-white p-8 text-center shadow-[0_25px_70px_rgba(15,23,42,0.12)] backdrop-blur dark:border-indigo-500/40 dark:from-indigo-950/30 dark:via-gray-900/50 dark:to-gray-900/60">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-indigo-100/70 bg-white/75 text-indigo-500 dark:border-indigo-500/40 dark:bg-gray-900/40 dark:text-indigo-200">
              <IconUsers className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("myEnvironment.classrooms.empty_title", { defaultValue: "No classrooms found" })}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {query
                ? t("myEnvironment.classrooms.empty_search", {
                    defaultValue: "No classrooms match your search.",
                  })
                : t("myEnvironment.classrooms.empty", {
                    defaultValue: "There are no classrooms linked to your account yet.",
                  })}
            </p>
          </div>
        )}

        {!loading && !error && activeClassrooms.length > 0 && (
          <div className="grid auto-rows-fr gap-4 md:grid-cols-2 xl:grid-cols-3">
            {activeClassrooms.map((classroom) => renderClassroomCard(classroom, getClassroomStatus(classroom)))}
          </div>
        )}

        {!loading && !error && previousClassrooms.length > 0 && (
          <section className="my-classrooms-enter rounded-[32px] border border-gray-100/70 bg-gradient-to-br from-gray-50/80 via-white to-white shadow-[0_25px_70px_rgba(15,23,42,0.10)] backdrop-blur dark:border-gray-600/40 dark:from-gray-900/60 dark:via-gray-900/50 dark:to-gray-900/60">
            <button
              type="button"
              onClick={() => setPreviousOpen((open) => !open)}
              className="lumen-glow-sheen flex w-full items-center justify-between gap-3 rounded-[32px] px-5 py-4 text-left"
            >
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t("myEnvironment.classrooms.previous_title", { defaultValue: "Previous classrooms" })}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("myEnvironment.classrooms.previous_count", {
                    defaultValue: "{{count}} previous classrooms",
                    count: previousClassrooms.length,
                  })}
                </p>
              </div>
              {previousOpen ? (
                <IconChevronDown className="h-5 w-5 text-gray-500" />
              ) : (
                <IconChevronRight className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {previousOpen && (
              <div className="border-t border-gray-100/70 p-5 dark:border-gray-700">
                <div className="grid auto-rows-fr gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {previousClassrooms.map((classroom) => renderClassroomCard(classroom, "previous"))}
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </LdsOverviewPage>
  );
};

export default MyClassrooms;
